import React from 'react';
import type { AttributeType, AttributeValue } from '@/api/models';
import {
    useCreateAttributeAliasMutation,
    useCreateAttributeValueMutation,
    useDeleteAttributeAliasMutation,
    useDeleteAttributeValueMutation,
    useReorderAttributeValuesMutation,
    useUpdateAttributeValueMutation,
} from '@/api/queries';
import type { AttributeValueFormValues } from '../attributeValueFormModal/useAttributeValueForm';

/**
 * Logic for the values drawer of one attribute type: value CRUD, reorder (via
 * the `values/order` endpoint), and per-value alias add/remove. `type` is
 * derived from the live types query upstream, so every mutation here (which
 * invalidates that query) flows back in as fresh `type.values`.
 */
export const useAttributeValuesSheet = (type: AttributeType | null) => {
    const createValueMutation = useCreateAttributeValueMutation();
    const updateValueMutation = useUpdateAttributeValueMutation();
    const deleteValueMutation = useDeleteAttributeValueMutation();
    const reorderMutation = useReorderAttributeValuesMutation();
    const createAliasMutation = useCreateAttributeAliasMutation();
    const deleteAliasMutation = useDeleteAttributeAliasMutation();

    const [isValueFormOpen, setIsValueFormOpen] = React.useState(false);
    const [editingValueId, setEditingValueId] = React.useState<string | null>(
        null,
    );
    const [valueFormError, setValueFormError] = React.useState<string | null>(
        null,
    );
    const [aliasDrafts, setAliasDrafts] = React.useState<
        Record<string, string>
    >({});

    // Reset transient state whenever the drawer's type changes — including to
    // `null` on close. Without this, closing the drawer mid-edit would leave
    // the value modal orphaned (type === null → submit is a no-op), and alias
    // drafts would linger across types. Keyed on `type?.id` so value/alias
    // mutations (which return a new type object with the same id) don't clobber
    // an in-progress edit.
    const typeId = type?.id ?? null;
    React.useEffect(() => {
        setIsValueFormOpen(false);
        setEditingValueId(null);
        setValueFormError(null);
        setAliasDrafts({});
    }, [typeId]);

    // Defensive sort — the API returns values ordered by position already.
    const values = React.useMemo<AttributeValue[]>(() => {
        if (!type) {
            return [];
        }
        return [...type.values].sort((a, b) => a.position - b.position);
    }, [type]);

    const editingValue = React.useMemo(() => {
        if (editingValueId === null) {
            return null;
        }
        return values.find((value) => value.id === editingValueId) ?? null;
    }, [editingValueId, values]);

    const valueFormInitialValues: AttributeValueFormValues | null =
        React.useMemo(() => {
            if (editingValue === null) {
                return null;
            }
            return { name: editingValue.name, slug: editingValue.slug };
        }, [editingValue]);

    const onAddValue = React.useCallback(() => {
        setEditingValueId(null);
        setValueFormError(null);
        setIsValueFormOpen(true);
    }, []);

    const onEditValue = React.useCallback((valueId: string) => {
        setEditingValueId(valueId);
        setValueFormError(null);
        setIsValueFormOpen(true);
    }, []);

    const onCloseValueForm = React.useCallback(() => {
        setIsValueFormOpen(false);
        setEditingValueId(null);
        setValueFormError(null);
    }, []);

    const onSubmitValue = React.useCallback(
        async (input: AttributeValueFormValues) => {
            if (!type) {
                return;
            }
            setValueFormError(null);
            // New values append to the end; edits keep their position.
            const position = editingValue?.position ?? values.length + 1;
            const body = { name: input.name, slug: input.slug, position };
            const result =
                editingValueId === null
                    ? await createValueMutation.mutateAsync({
                          typeId: type.id,
                          body,
                      })
                    : await updateValueMutation.mutateAsync({
                          typeId: type.id,
                          valueId: editingValueId,
                          body,
                      });
            if (!result.success) {
                setValueFormError(
                    result.error.message ?? 'Failed to save value.',
                );
                return;
            }
            setIsValueFormOpen(false);
            setEditingValueId(null);
        },
        [
            createValueMutation,
            editingValue,
            editingValueId,
            type,
            updateValueMutation,
            values.length,
        ],
    );

    const onDeleteValue = React.useCallback(
        (valueId: string) => {
            if (!type) {
                return;
            }
            deleteValueMutation.mutate({ typeId: type.id, valueId });
        },
        [deleteValueMutation, type],
    );

    const onMoveValue = React.useCallback(
        (valueId: string, direction: 'up' | 'down') => {
            if (!type) {
                return;
            }
            const ordered = [...values];
            const from = ordered.findIndex((value) => value.id === valueId);
            const to = direction === 'up' ? from - 1 : from + 1;
            if (from < 0 || to < 0 || to >= ordered.length) {
                return;
            }
            const moved = ordered[from]!;
            ordered[from] = ordered[to]!;
            ordered[to] = moved;
            reorderMutation.mutate({
                typeId: type.id,
                body: { valueIds: ordered.map((value) => value.id) },
            });
        },
        [reorderMutation, type, values],
    );

    const onAliasDraftChange = React.useCallback(
        (valueId: string, text: string) => {
            setAliasDrafts((drafts) => ({ ...drafts, [valueId]: text }));
        },
        [],
    );

    const onAddAlias = React.useCallback(
        (valueId: string) => {
            if (!type) {
                return;
            }
            const alias = (aliasDrafts[valueId] ?? '').trim();
            if (!alias) {
                return;
            }
            createAliasMutation.mutate(
                { typeId: type.id, valueId, body: { alias } },
                {
                    onSuccess: (result) => {
                        if (result.success) {
                            setAliasDrafts((drafts) => ({
                                ...drafts,
                                [valueId]: '',
                            }));
                        }
                    },
                },
            );
        },
        [aliasDrafts, createAliasMutation, type],
    );

    const onDeleteAlias = React.useCallback(
        (valueId: string, aliasId: string) => {
            if (!type) {
                return;
            }
            deleteAliasMutation.mutate({ typeId: type.id, valueId, aliasId });
        },
        [deleteAliasMutation, type],
    );

    return {
        values,
        // value form
        isValueFormOpen,
        valueFormMode:
            editingValueId === null ? ('create' as const) : ('edit' as const),
        valueFormInitialValues,
        valueFormError,
        isValueSubmitting:
            createValueMutation.isPending || updateValueMutation.isPending,
        onAddValue,
        onEditValue,
        onCloseValueForm,
        onSubmitValue,
        onDeleteValue,
        // reorder
        onMoveValue,
        isReordering: reorderMutation.isPending,
        // aliases
        aliasDrafts,
        onAliasDraftChange,
        onAddAlias,
        onDeleteAlias,
    };
};
